import { Link } from "react-router-dom";
import { ArrowRight, Moon, Sun } from "lucide-react";
import Seo from "../components/Seo";
import { ESSEX_AREAS, LONDON_AREAS, IMAGES } from "../config/site";

const ROUTE_LINKS = [
  { to: "/great-dunmow-to-london", testid: "areas-route-dunmow", label: "Great Dunmow to London" },
  { to: "/felsted-flitch-green-to-london", testid: "areas-route-felsted", label: "Felsted & Flitch Green to London" },
  { to: "/bishops-stortford-to-london", testid: "areas-route-stortford", label: "Bishop's Stortford to London" },
  { to: "/stansted-mountfitchet-takeley-to-london", testid: "areas-route-stansted", label: "Stansted Mountfitchet & Takeley to London" },
];

export default function AreasCovered() {
  return (
    <main data-testid="areas-page">
      <Seo
        title="Areas Covered — Essex M11 Corridor ↔ London"
        description="Fixed £100 Black Cab journeys between Great Dunmow, Felsted, Bishop's Stortford, Stansted, Sawbridgeworth, Harlow and the City, Liverpool Street, Bank, Canary Wharf and Central London."
      />

      <section className="border-b border-[#27272A]">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">Areas covered</p>
          <h1 className="mt-4 max-w-3xl text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
            The Essex ↔ London M11 corridor
          </h1>
          <p className="mt-5 max-w-2xl leading-relaxed text-[#A1A1AA]">
            We operate two journey directions only: Essex → London and London → Essex. Every
            request is considered on its own merits — we never reject a request automatically
            based on distance within this service area. Drivers decide which journeys are worth
            accepting.
          </p>
        </div>
      </section>

      <section className="section-pad mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="border border-[#27272A] bg-[#111111]" data-testid="essex-areas-card">
            <img src={IMAGES.essex.url} alt={IMAGES.essex.alt} className="h-56 w-full object-cover" loading="lazy" />
            <div className="p-8">
              <h2 className="text-2xl font-semibold text-white">Essex pickup &amp; drop-off areas</h2>
              <ul className="mt-5 space-y-3">
                {ESSEX_AREAS.map((area) => (
                  <li key={area} className="flex items-center gap-3 text-[#D4D4D8]">
                    <span className="h-px w-6 bg-[#D4AF37]" aria-hidden="true" />
                    {area}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="border border-[#27272A] bg-[#111111]" data-testid="london-areas-card">
            <img src={IMAGES.london.url} alt={IMAGES.london.alt} className="h-56 w-full object-cover" loading="lazy" />
            <div className="p-8">
              <h2 className="text-2xl font-semibold text-white">London pickup &amp; drop-off areas</h2>
              <ul className="mt-5 space-y-3">
                {LONDON_AREAS.map((area) => (
                  <li key={area} className="flex items-center gap-3 text-[#D4D4D8]">
                    <span className="h-px w-6 bg-[#D4AF37]" aria-hidden="true" />
                    {area}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-10">
          <h2 className="text-xl font-semibold text-white">Popular route pages</h2>
          <ul className="mt-4 grid gap-3 sm:grid-cols-2">
            {ROUTE_LINKS.map((r) => (
              <li key={r.to}>
                <Link
                  to={r.to}
                  data-testid={r.testid}
                  className="focus-gold group flex items-center justify-between gap-3 border border-[#27272A] bg-[#111111] px-5 py-4 text-sm font-semibold text-white transition-colors duration-150 hover:border-[rgba(212,175,55,0.4)] hover:text-[#D4AF37]"
                >
                  {r.label}
                  <ArrowRight className="h-4 w-4 shrink-0 text-[#D4AF37]" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-2">
          <div className="border border-[#27272A] bg-[#111111] p-8" data-testid="direction-morning-card">
            <Sun className="h-7 w-7 text-[#D4AF37]" aria-hidden="true" />
            <h2 className="mt-4 text-xl font-semibold text-white">Essex → London</h2>
            <p className="mt-2 text-sm leading-relaxed text-[#A1A1AA]">
              Mainly early morning until late afternoon — ideal for commuters, City meetings,
              hospital appointments and days out in town.
            </p>
          </div>
          <div className="border border-[#27272A] bg-[#111111] p-8" data-testid="direction-evening-card">
            <Moon className="h-7 w-7 text-[#D4AF37]" aria-hidden="true" />
            <h2 className="mt-4 text-xl font-semibold text-white">London → Essex</h2>
            <p className="mt-2 text-sm leading-relaxed text-[#A1A1AA]">
              Mainly early evening until late night — travel home in comfort after work, dinner
              or a show. Drivers keep flexible schedules, so other times are always worth asking
              about.
            </p>
          </div>
        </div>

        <p className="mt-10 text-sm leading-relaxed text-[#71717A]">
          During this initial trial we do not offer journeys entirely within Essex or entirely
          within London, airport-arrival collections or nationwide bookings. One end of your
          journey must be in our Essex area and the other in London.
        </p>

        <Link
          to="/book"
          data-testid="areas-cta"
          className="focus-gold mt-10 inline-flex items-center gap-2 border border-[#D4AF37] bg-[#D4AF37] px-8 py-4 text-sm font-semibold uppercase tracking-wider text-black transition-colors duration-150 hover:bg-[#E5C158]"
        >
          Request a £100 Journey <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </section>
    </main>
  );
}
